import type { MatchState, PlayerState } from './state';
import type { SimConfig } from '../config/simConfig';
import type { SimEvent } from './events';
import { PITCH } from '../config/pitchConfig';
import { rngStep } from '../math/rng';

// Goalkeeper mechanics (§5.4/§5.6). GK_DIVE start-eligibility (own box, keeper or anyone when teamSize < 3
// per P1) is enforced in the action table; here live the catch/parry capsule for a diving keeper. Pure —
// no I/O, no RNG.

/** True if player `p` is inside its own penalty box (own goal end, ±penalty-box half-width). */
export function inOwnBox(state: MatchState, p: PlayerState): boolean {
  const pitch = PITCH[state.mode.teamSize];
  const dir = state.attackDir[p.team];
  const ownGoalX = -dir * (pitch.length / 2);
  const xFromGoal = dir * (p.pos.x - ownGoalX); // 0 at own goal line, grows toward midfield
  return xFromGoal >= 0 && xFromGoal <= pitch.penaltyBoxDepth && Math.abs(p.pos.y) <= pitch.penaltyBoxHalfWidth;
}

/** May this player begin a GK_DIVE from its current position? (§5.4 / P1) */
export function canGkDive(state: MatchState, p: PlayerState): boolean {
  const gkRole = p.isGk || state.mode.teamSize < 3; // P1: no dedicated keeper below 3-a-side ⇒ anyone dives
  return gkRole && inOwnBox(state, p);
}

/**
 * Step ~5e of §5.1 — catch / parry for keepers mid-GK_DIVE. During the catch window a ball within reach is
 * grabbed (speed < CATCH_MAX ⇒ GK_HOLD + SAVE{held:true}) or parried away (≥ CATCH_MAX ⇒ reflect with
 * restitution PARRY_E, SAVE{held:false}). Runs after collisions, before possession. Mutates `state`.
 */
/**
 * D133 (§4, ✅ user: "dives happen automatically outside penalties") — start the D125 intercept dive when
 * the shot's crossing point is beyond standing reach but inside dive reach and NOBODY dove for it.
 *
 * A human-held keeper had no auto-dive at all: automatic saves are standing-reach only (D39) and the dive
 * bits shipped unbound, so every ball a step wide of him was a goal by default. This is a FALLBACK, not a
 * replacement — an input-initiated dive suppresses it, so the bots' `gkSkillMul` dive-timing channel and
 * §18 T-ACT-009's meaning are both untouched (PT4-P21: the penalty-only restriction on the dive KEYS is
 * a client-mapper rule, never a sim rule). Draws no rng.
 */
export function resolveGkAutoDive(state: MatchState, cfg: SimConfig): void {
  const gk = cfg.actions.gk;
  if (gk.AUTO_DIVE !== 1) return;
  const b = state.ball;
  if (b.possessor !== null) return;
  const speedX = Math.abs(b.vel.x);
  if (speedX < 1e-3) return;
  for (const p of state.players) {
    if (p.sentOff || !p.isGk) continue;
    if (p.action !== 'IDLE' && p.action !== 'RUN') continue; // never out of SMOTHER/PRONE/GETUP/RAGDOLL/GK_HOLD
    if (!inOwnBox(state, p) || b.gkLock === p.id) continue;
    const dir = state.attackDir[p.team];
    if (dir * b.vel.x >= 0) continue; // travelling AWAY from his goal — nothing to save
    const tt = Math.abs(b.pos.x - p.pos.x) / speedX; // seconds until it crosses his line
    if (tt <= 0 || tt > 1.2) continue;
    const yAt = b.pos.y + b.vel.y * tt;
    // grounded: a rolling ball stays on the deck rather than going "negative" under gravity — the same
    // `Math.max(0, …)` the D125 input dive solves its crossing height with.
    const zAt = Math.max(0, b.pos.z + b.vel.z * tt - 0.5 * cfg.ball.GRAVITY * tt * tt);
    const need = Math.hypot(yAt - p.pos.y, Math.max(0, zAt - gk.DIVE_HAND_Z));
    // the ANNULUS: too close and the standing hands already have it, too far and no dive reaches it
    if (need <= gk.STANDING_REACH || need > gk.CATCH_REACH) continue;
    const pitch = PITCH[state.mode.teamSize];
    if (Math.abs(yAt) > pitch.goalWidth / 2 + cfg.rules.SHOT_MARGIN) continue; // not goal-bound
    p.action = 'GK_DIVE';
    p.actionTicks = 0;
    p.charge = null;
    const side = yAt >= p.pos.y ? 1 : -1;
    p.vel.x = 0;
    p.vel.y = Math.max(-gk.DIVE_SPEED, Math.min(gk.DIVE_SPEED, (yAt - p.pos.y) / Math.max(tt, 1e-3)));
    if (p.vel.y === 0) p.vel.y = side * 0.01; // keep the dive's SIDE well-defined for the render
    if (zAt > gk.DIVE_HAND_Z) {
      const needVz = (zAt - gk.DIVE_HAND_Z) / Math.max(tt, 1e-3) + 0.5 * cfg.ball.GRAVITY * tt;
      p.vz = Math.min(gk.DIVE_VZ_MAX, Math.max(0, needVz));
    }
    return; // one keeper, one dive
  }
}

export function resolveGkSave(state: MatchState, cfg: SimConfig, events: SimEvent[]): void {
  const b = state.ball;
  if (b.possessor !== null) return; // already held (a keeper catch this tick would already have set it)
  const gk = cfg.actions.gk;
  const [w0, w1] = gk.CATCH_WINDOW;
  const capH = cfg.collision.CAPSULE_H;
  for (const p of state.players) {
    if (p.sentOff) continue;
    // D39: saves are AUTOMATIC — a keeper standing in his box attempts every save within
    // STANDING_REACH with no button (a human keeper steers position; the sim does the hands).
    // A GK_DIVE (bots, or an ambitious human) extends the reach to CATCH_REACH in its window.
    const diving = p.action === 'GK_DIVE' && p.actionTicks >= w0 && p.actionTicks <= w1;
    const standing =
      p.isGk &&
      (p.action === 'IDLE' || p.action === 'RUN') &&
      inOwnBox(state, p) &&
      b.gkLock !== p.id && // the re-handle lock still forbids hands (§5.6/D18)
      b.vel.x * (p.pos.x - b.pos.x) + b.vel.y * (p.pos.y - b.pos.y) > 0; // ball coming AT him
    if (!diving && !standing) continue;
    const dx = b.pos.x - p.pos.x;
    const dy = b.pos.y - p.pos.y;
    const horiz = Math.hypot(dx, dy);
    // D125: the catch capsule rides WITH him. A dive that jumps (p.z > 0) reaches a ball a standing
    // keeper cannot — otherwise the render shows him getting a hand to it and the sim says he missed.
    const handZ = p.z + (diving ? gk.DIVE_HAND_Z : 0);
    const ceiling = Math.max(capH, handZ + gk.CATCH_REACH * 0.5);
    if (horiz > (diving ? gk.CATCH_REACH : gk.STANDING_REACH) || b.pos.z > ceiling) continue;
    const speed = Math.hypot(b.vel.x, b.vel.y, b.vel.z);
    // D35 catch-quality ladder (replaces binary CATCH_MAX): react (flight time the keeper had) +
    // reach demand (offset within the catch capsule) + shot power → HOLD / PARRY / BEATEN.
    // Seeded jitter applies ONLY in the parry band (one state.rng draw per save attempt — before
    // referee draws in the tick order; deterministic + replay-safe).
    const flightTicks = b.lastTouch ? state.tick - b.lastTouch.tick : 99;
    const react = Math.min(flightTicks / gk.REACT_FULL_TICKS, 1);
    const demand = Math.min(horiz / gk.CATCH_REACH, 1);
    const power = Math.min(speed / cfg.actions.kick.MAX.SHOOT, 1);
    let q = (gk.Q_W_REACT * react + gk.Q_W_DEMAND * (1 - demand) + gk.Q_W_POWER * (1 - power)) * gk.SKILL;
    if (!diving) q *= gk.STANDING_Q_MUL; // D39: no-dive saves are parry-prone — rebounds stay in play
    if (q < gk.Q_HOLD && q >= gk.Q_PARRY - gk.PARRY_JITTER) {
      const { value, state: rs } = rngStep(state.rng); // parry-band jitter (D35)
      state.rng = rs;
      q += (value * 2 - 1) * gk.PARRY_JITTER;
    }
    if (q < gk.Q_PARRY) {
      // beaten — the keeper gets nothing on it; the shot continues untouched
      continue;
    }
    if (q >= gk.Q_HOLD && speed < gk.CATCH_MAX) {
      // catch: possess and enter GK_HOLD; the ball glues to the keeper
      b.possessor = p.id;
      b.vel.x = 0;
      b.vel.y = 0;
      b.vel.z = 0;
      b.pos.z = 0;
      b.lastTouch = { entity: p.id, team: p.team, tick: state.tick };
      b.shotPending = false;
      p.action = 'GK_HOLD';
      p.actionTicks = 0;
      p.charge = null;
      events.push({ t: 'SAVE', keeper: p.id, held: true });
    } else {
      // parry (D35): reflect about the keeper→ball normal, damped — a live rebound into the box
      const nx = horiz > 1e-6 ? dx / horiz : 1;
      const ny = horiz > 1e-6 ? dy / horiz : 0;
      const vn = b.vel.x * nx + b.vel.y * ny;
      if (vn < 0) {
        const j = -(1 + gk.PARRY_E) * vn;
        b.vel.x += j * nx;
        b.vel.y += j * ny;
      }
      b.vel.x *= gk.PARRY_SPEED_MUL + 0.6; // damp the rebound (1.0 → PARRY_SPEED_MUL+0.6 of post-reflect speed)
      b.vel.y *= gk.PARRY_SPEED_MUL + 0.6;
      b.vel.z *= 0.5;
      // D48: parry WIDE — keepers are coached to push shots AROUND the frame. A rebound that still
      // points into the own goal mouth (the residual "keeper patted it into his own net") is steered
      // to the goal line just outside the nearer post, same speed. Deterministic, no draw.
      {
        const pitch = PITCH[state.mode.teamSize];
        const dir = state.attackDir[p.team];
        const ownGoalX = -dir * (pitch.length / 2);
        const sp = Math.hypot(b.vel.x, b.vel.y);
        if (sp > 1e-6 && dir * b.vel.x < 0) {
          const yAtLine = b.pos.y + b.vel.y * ((ownGoalX - b.pos.x) / b.vel.x);
          const halfW = pitch.goalWidth / 2;
          if (Math.abs(yAtLine) < halfW + 0.5) {
            const wideY = (yAtLine >= 0 ? 1 : -1) * (halfW + 1.2);
            const yaw = Math.atan2(wideY - b.pos.y, ownGoalX - b.pos.x);
            b.vel.x = sp * Math.cos(yaw);
            b.vel.y = sp * Math.sin(yaw);
          }
        }
      }
      b.shotPending = false;
      b.lastTouch = { entity: p.id, team: p.team, tick: state.tick };
      events.push({ t: 'SAVE', keeper: p.id, held: false });
    }
    return; // one save per tick
  }
}

// ── D18: keeper ball-handling (pickup / smother / re-handle lock / box exit) ──

/** P1 keeper role: the dedicated GK, or anyone below 3-a-side. */
export function gkRole(state: MatchState, p: PlayerState): boolean {
  return p.isGk || state.mode.teamSize < 3;
}

/** May `p` pick up the loose ball right now? (own box, keeper role, slow low ball in reach, not locked). */
export function canGkPickup(state: MatchState, p: PlayerState, cfg: SimConfig): boolean {
  const b = state.ball;
  if (b.possessor !== null || b.gkLock === p.id) return false;
  if (!gkRole(state, p) || !inOwnBox(state, p)) return false;
  if (b.pos.z >= 1 || Math.hypot(b.vel.x, b.vel.y, b.vel.z) >= cfg.possession.CLAIM_MAX_SPEED) return false;
  return Math.hypot(b.pos.x - p.pos.x, b.pos.y - p.pos.y) <= cfg.actions.gk.PICKUP_REACH;
}

/** Grab the loose ball into GK_HOLD (instant pickup — D18). Caller has checked canGkPickup. */
export function gkPickup(state: MatchState, p: PlayerState, events: SimEvent[]): void {
  const b = state.ball;
  b.possessor = p.id;
  b.vel.x = 0;
  b.vel.y = 0;
  b.vel.z = 0;
  b.pos.z = 0;
  b.shotPending = false;
  b.lastTouch = { entity: p.id, team: p.team, tick: state.tick };
  p.action = 'GK_HOLD';
  p.actionTicks = 0;
  p.charge = null;
  events.push({ t: 'GK_PICKUP', keeper: p.id });
}

/**
 * GK_SMOTHER resolution (D18): during the active window, an opponent-carried ball within reach — reduced
 * vs a SHIELDing carrier — is smothered into GK_HOLD, IF the keeper comes at the ball's exposed side
 * (not through the carrier's body). Whiff is handled by the action table (window expires ⇒ PRONE).
 * D48: hands are automatic at the feet too — a STANDING dedicated keeper (isGk; the dive verb stays
 * available to everyone < 3-a-side) in his box wins a carried ball the moment it enters smother reach,
 * no button needed. Walking the ball into the goal must never beat a set keeper; the counterplay is to
 * shoot first, chip him, or keep your body between him and the ball (the exposed-side test below).
 */
export function resolveGkSmother(state: MatchState, cfg: SimConfig, events: SimEvent[]): void {
  const b = state.ball;
  if (b.possessor === null) return;
  const carrier = state.players[b.possessor];
  if (!carrier) return;
  const gk = cfg.actions.gk;
  const [w0, w1] = gk.SMOTHER_WINDOW;
  for (const p of state.players) {
    if (p.sentOff) continue;
    const diving = p.action === 'GK_SMOTHER' && p.actionTicks >= w0 && p.actionTicks <= w1;
    const standing = p.isGk && (p.action === 'IDLE' || p.action === 'RUN') && state.phase === 'PLAY'; // D48 auto path
    if (!diving && !standing) continue;
    if (carrier.team === p.team || b.gkLock === p.id || !inOwnBox(state, p)) continue;
    const reach = carrier.action === 'SHIELD' ? gk.SMOTHER_REACH_SHIELDED : gk.SMOTHER_REACH;
    const dx = b.pos.x - p.pos.x;
    const dy = b.pos.y - p.pos.y;
    if (Math.hypot(dx, dy) > reach) continue;
    // exposed-side test: keeper→ball must not point back through the carrier (dot(ball→keeper, carrier→ball) > −0.2)
    const cbx = b.pos.x - carrier.pos.x;
    const cby = b.pos.y - carrier.pos.y;
    const cbLen = Math.hypot(cbx, cby);
    const kbLen = Math.hypot(dx, dy);
    if (cbLen > 1e-6 && kbLen > 1e-6) {
      const dot = (-dx / kbLen) * (cbx / cbLen) + (-dy / kbLen) * (cby / cbLen);
      if (dot < -0.2) continue; // reaching through the carrier's body — no win
    }
    const victim = carrier.id;
    b.possessor = p.id;
    b.vel.x = 0;
    b.vel.y = 0;
    b.vel.z = 0;
    b.pos.z = 0;
    b.shotPending = false;
    b.lastTouch = { entity: p.id, team: p.team, tick: state.tick };
    p.action = 'GK_HOLD';
    p.actionTicks = 0;
    p.charge = null;
    events.push({ t: 'GK_SMOTHER', keeper: p.id, victim });
    return; // one smother per tick
  }
}

/** Box exit while holding (D18): GK_HOLD outside own box ⇒ seamless carried possession + re-handle lock. */
export function resolveGkBoxExit(state: MatchState): void {
  const b = state.ball;
  if (b.possessor === null) return;
  const p = state.players[b.possessor];
  if (!p || p.action !== 'GK_HOLD') return;
  if (!inOwnBox(state, p)) {
    p.action = 'RUN';
    p.actionTicks = 0;
    b.gkLock = p.id;
  }
}

/** Clear the re-handle lock once ANY other player touches the ball (D18). A stale lastTouch from
 *  BEFORE the lock was set must not clear it — only a fresh touch (this tick) counts. */
export function clearGkLock(state: MatchState): void {
  const b = state.ball;
  if (b.gkLock !== null && b.lastTouch && b.lastTouch.entity !== b.gkLock && b.lastTouch.tick === state.tick) b.gkLock = null;
}
